import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import useAdoptableState from '../../../AdoptableInfo/useAdoptableState';
import Card from '../../lego/Card';
import CardHeading from '../../lego/CardHeading';
import Description from '../../lego/Description';
import BuyerButtons from './BuyerButtons';
import SellerButtons from './SellerButtons';
import Countdown from '../../lego/Countdown';
import MinimumOffer from '../../lego/MinimumOffer';
import MinOfferAndCountdown from '../../lego/MinOfferAndCountdown';
import OfferList from '../../lego/OfferList';
import Stack from '../../lego/Stack';
import TradeCount from '../../lego/TradeCount';
import ResaleIndicator from '../../lego/ResaleIndicator';
import { useExclusiveCategoryTranslation } from '../../hooks';
import s from './AdoptableForSale.scss';
function getDescriptionTranslation(t, { isSeller, canAdopt }) {
    if (isSeller) {
        return t('sidebar.exclusives.description.seller');
    } else if (canAdopt) {
        return t('sidebar.exclusives.description.buyer');
    } else {
        return t('sidebar.exclusives.description.other');
    }
}
/**
 * An adoptable specific variant of our exclusive sidebar card. Used only when the exclusive is currently listed for sale.
 *
 * NOTE: This component is used for all exclusive types as of this comment's writing. There is no difference spec'd between
 * these cards. Because I don't believe this will stay the case, I architected this in a way to make it easy to make a brand-new
 * sidebar card component.
 *
 * If an exclusive category's card features start to diverge, make a new component instead of putting it in here.
 */ const AdoptableForSale = ({ deviation, className })=>{
    var _deviation_extended_adoptable, _deviation_extended;
    const { t } = useTranslation();
    const exclusiveCategoryName = useExclusiveCategoryTranslation(deviation);
    const adoptableState = useAdoptableState(deviation);
    const isExclusiveGifts = useFlag('exclusive_gifts');
    const { isSeller, canSeeMinOfferPrice, canSeeCountdown, hasOffers, offersList, minOfferPrice, saleCount, offerEndTime, offerTimerHasEnded, onTimerEnd } = adoptableState;
    if (isExclusiveGifts) {
        var _deviation_extended_adoptable1, _deviation_extended1;
        return /*#__PURE__*/ React.createElement(Card, {
            badgeType: "adoptable",
            className: className
        }, /*#__PURE__*/ React.createElement(Stack, {
            gap: 16
        }, /*#__PURE__*/ React.createElement(CardHeading, null, exclusiveCategoryName), /*#__PURE__*/ React.createElement(Description, null, getDescriptionTranslation(t, adoptableState)), !!saleCount && /*#__PURE__*/ React.createElement(TradeCount, {
            className: s['trade-count'],
            deviation: deviation,
            count: saleCount
        }), !saleCount && /*#__PURE__*/ React.createElement(ResaleIndicator, {
            isResellable: !!((_deviation_extended1 = deviation.extended) === null || _deviation_extended1 === void 0 ? void 0 : (_deviation_extended_adoptable1 = _deviation_extended1.adoptable) === null || _deviation_extended_adoptable1 === void 0 ? void 0 : _deviation_extended_adoptable1.resaleAllowed)
        }), isSeller ? /*#__PURE__*/ React.createElement(SellerButtons, {
            deviation: deviation,
            adoptableState: adoptableState
        }) : /*#__PURE__*/ React.createElement(BuyerButtons, {
            deviation: deviation,
            adoptableState: adoptableState
        }), canSeeCountdown && canSeeMinOfferPrice && /*#__PURE__*/ React.createElement(MinOfferAndCountdown, {
            adoptableState: adoptableState
        }), !canSeeCountdown && canSeeMinOfferPrice && /*#__PURE__*/ React.createElement(MinimumOffer, {
            value: minOfferPrice
        }), /*#__PURE__*/ React.createElement(Stack, {
            gap: 8
        }, canSeeCountdown && !canSeeMinOfferPrice && /*#__PURE__*/ React.createElement(Countdown, {
            hasEnded: offerTimerHasEnded,
            offerEndTime: offerEndTime,
            onEnd: onTimerEnd
        }), hasOffers && /*#__PURE__*/ React.createElement(OfferList, {
            deviation: deviation,
            offers: offersList
        }))));
    }
    return /*#__PURE__*/ React.createElement(Card, {
        badgeType: "adoptable",
        className: className
    }, /*#__PURE__*/ React.createElement(Stack, {
        gap: 16
    }, /*#__PURE__*/ React.createElement(CardHeading, null, exclusiveCategoryName), /*#__PURE__*/ React.createElement(Description, null, getDescriptionTranslation(t, adoptableState)), !!saleCount && /*#__PURE__*/ React.createElement(TradeCount, {
        className: s['trade-count'],
        deviation: deviation,
        count: saleCount
    }), !saleCount && /*#__PURE__*/ React.createElement(ResaleIndicator, {
        isResellable: !!((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : _deviation_extended_adoptable.resaleAllowed)
    }), isSeller ? /*#__PURE__*/ React.createElement(SellerButtons, {
        deviation: deviation,
        adoptableState: adoptableState
    }) : /*#__PURE__*/ React.createElement(BuyerButtons, {
        deviation: deviation,
        adoptableState: adoptableState
    }), offerEndTime && canSeeMinOfferPrice && /*#__PURE__*/ React.createElement(MinOfferAndCountdown, {
        adoptableState: adoptableState
    }), !offerEndTime && canSeeMinOfferPrice && /*#__PURE__*/ React.createElement(MinimumOffer, {
        value: minOfferPrice
    }), /*#__PURE__*/ React.createElement(Stack, {
        gap: 8
    }, offerEndTime && !canSeeMinOfferPrice && /*#__PURE__*/ React.createElement(Countdown, {
        hasEnded: offerTimerHasEnded,
        offerEndTime: offerEndTime,
        onEnd: onTimerEnd
    }), hasOffers && /*#__PURE__*/ React.createElement(OfferList, {
        deviation: deviation,
        offers: offersList
    }))));
};
AdoptableForSale.displayName = 'AdoptableForSale';
export default AdoptableForSale;
